import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TarActionItemCode } from '@ekhodealer/ekho-common';

import type { AssignMotorcycleEngineNumberResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Input type for assigning a motorcycle engine number to an order
 */
export interface AssignMotorcycleEngineNumberInput {
	orderId: string;
	motorcycleEngineNumber: string;
}

/**
 * API function to assign a motorcycle engine number to a purchased vehicle
 */
export const assignMotorcycleEngineNumber = async (
	servicesProviderId: string,
	{ orderId, motorcycleEngineNumber }: AssignMotorcycleEngineNumberInput,
): Promise<AssignMotorcycleEngineNumberResponse> => {
	const response = await apiClient.post<AssignMotorcycleEngineNumberResponse>(
		ordersEndpoints.assignMotorcycleEngineNumber(servicesProviderId, orderId),
		{ motorcycleEngineNumber },
	);
	return response.data;
};

/**
 * Hook for assigning a motorcycle engine number to a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useAssignMotorcycleEngineNumber = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: Omit<AssignMotorcycleEngineNumberInput, 'orderId'>) => {
			if (!orderId || !servicesProviderId) {
				throw new Error('Unable to assign motorcycle engine number without an order ID or services provider ID.');
			}

			return assignMotorcycleEngineNumber(servicesProviderId, {
				orderId,
				motorcycleEngineNumber: input.motorcycleEngineNumber,
			});
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, TarActionItemCode.ASSIGN_MOTORCYCLE_ENGINE_NUMBER);
		},
	});
};
